import React, { useContext, useEffect, useReducer, useState } from "react";
import { toast } from "react-toastify";
import {
  Button,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import VisibilityIcon from "@material-ui/icons/Visibility";
import openSocket from "../../services/socket-io";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import CompanyModal from "../../components/CompanyModal";
import CompanyDetailsDialog from "../../components/CompanyDetailsDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";

const SummaryCard = ({ label, value }) => (
  <Paper variant="outlined" style={{ padding: 16, height: "100%" }}>
    <div style={{ fontSize: 13, color: "#666" }}>{label}</div>
    <div style={{ fontSize: 28, fontWeight: 700 }}>{value}</div>
  </Paper>
);

const formatCurrency = value =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(Number(value || 0));

const formatDateTime = value => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const formatBytes = value => {
  const size = Number(value || 0);
  if (!size) {
    return "0 B";
  }

  const units = ["B", "KB", "MB", "GB", "TB"];
  const index = Math.min(
    units.length - 1,
    Math.floor(Math.log(size) / Math.log(1024))
  );
  const normalized = size / Math.pow(1024, index);

  return `${normalized.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

const normalizeBoolean = value => value === true || value === "true" || value === 1;

const reducer = (state, action) => {
  if (action.type === "LOAD_COMPANIES") {
    return [...action.payload];
  }

  if (action.type === "UPSERT_COMPANY") {
    const company = action.payload;
    const companyIndex = state.findIndex(item => item.id === company.id);

    if (companyIndex !== -1) {
      state[companyIndex] = company;
      return [...state];
    }

    return [company, ...state];
  }

  if (action.type === "REMOVE_COMPANY") {
    return state.filter(item => item.id !== action.payload);
  }

  return state;
};

const Companies = () => {
  const { user } = useContext(AuthContext);
  const [companies, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsCompanyId, setDetailsCompanyId] = useState(null);
  const [companyActionLoading, setCompanyActionLoading] = useState(false);
  const [confirmationState, setConfirmationState] = useState({
    open: false,
    mode: null,
    company: null
  });
  const [summary, setSummary] = useState({
    totalCompanies: 0,
    activeCompanies: 0,
    archivedCompanies: 0,
    trialCompanies: 0,
    pastDueCompanies: 0,
    suspendedCompanies: 0,
    activeMrr: 0,
    overdueAmount: 0,
    renewalsDueSoon: 0,
    trialsEndingSoon: 0
  });
  const [installationLicenseStatus, setInstallationLicenseStatus] = useState(null);
  const [companyPlans, setCompanyPlans] = useState([]);
  const [mediaStorage, setMediaStorage] = useState({
    totalFiles: 0,
    totalBytes: 0,
    oldFiles: 0,
    oldBytes: 0,
    thresholdDays: 90
  });
  const [publicMediaStorage, setPublicMediaStorage] = useState({
    enabled: true,
    provider: "local",
    bucket: "",
    region: "us-east-1",
    endpoint: "",
    accessKeyId: "",
    secretAccessKey: "",
    forcePathStyle: false,
    prefix: "public",
    publicBaseUrl: "",
    deleteLocalAfterUpload: false
  });
  const [recordingStorage, setRecordingStorage] = useState({
    enabled: false,
    provider: "local",
    bucket: "",
    region: "us-east-1",
    endpoint: "",
    accessKeyId: "",
    secretAccessKey: "",
    forcePathStyle: false,
    prefix: "companies",
    signedUrlExpiresSeconds: 300,
    deleteLocalAfterUpload: false
  });
  const normalizeMediaStorage = data => ({
    totalFiles: Number(data?.totalFiles || 0),
    totalBytes: Number(data?.totalBytes || 0),
    oldFiles: Number(data?.oldFiles || 0),
    oldBytes: Number(data?.oldBytes || 0),
    thresholdDays: Number(data?.thresholdDays || 90)
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const companiesResponse = await api.get("/companies");

        dispatch({
          type: "LOAD_COMPANIES",
          payload: companiesResponse.data
        });
        setLoading(false);

        const [
          summaryResult,
          installationLicenseResult,
          mediaStorageResult,
          publicMediaStorageResult,
          companyPlansResult,
          recordingStorageResult
        ] = await Promise.allSettled([
          api.get("/companies/dashboard/summary"),
          api.get("/licenses/installation/status"),
          api.get("/companies/media-storage/summary", {
            params: { thresholdDays: 90 }
          }),
          api.get("/companies/media-storage/settings"),
          api.get("/companies/plans"),
          api.get("/companies/call-recording/storage")
        ]);

        if (summaryResult.status === "fulfilled") {
          setSummary(summaryResult.value.data);
        }

        if (installationLicenseResult.status === "fulfilled") {
          setInstallationLicenseStatus(installationLicenseResult.value.data || null);
        }

        if (mediaStorageResult.status === "fulfilled") {
          setMediaStorage(normalizeMediaStorage(mediaStorageResult.value.data));
        }

        if (publicMediaStorageResult.status === "fulfilled") {
          setPublicMediaStorage(current => ({
            ...current,
            ...(publicMediaStorageResult.value.data || {})
          }));
        }

        if (companyPlansResult.status === "fulfilled") {
          setCompanyPlans(
            Array.isArray(companyPlansResult.value.data)
              ? companyPlansResult.value.data
              : []
          );
        }

        if (recordingStorageResult.status === "fulfilled") {
          setRecordingStorage(current => ({
            ...current,
            ...(recordingStorageResult.value.data || {})
          }));
        }
      } catch (err) {
        const errorMsg = err?.response?.data?.error || err?.response?.data?.message;
        if (user?.profile !== "superadmin" && errorMsg === "ERR_NO_PERMISSION") {
          return;
        }
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.profile]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("company", data => {
      if (data.action === "create" || data.action === "update") {
        dispatch({ type: "UPSERT_COMPANY", payload: data.company });
        api
          .get("/companies/dashboard/summary")
          .then(({ data: summaryData }) => setSummary(summaryData))
          .catch(() => undefined);
      }

      if (data.action === "delete") {
        dispatch({ type: "REMOVE_COMPANY", payload: data.company.id });
        api
          .get("/companies/dashboard/summary")
          .then(({ data: summaryData }) => setSummary(summaryData))
          .catch(() => undefined);
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedCompany(null);
  };

  const handleOpenCreate = () => {
    setSelectedCompany(null);
    setModalOpen(true);
  };

  const handleEdit = company => {
    setSelectedCompany(company);
    setModalOpen(true);
  };

  const handleOpenDetails = companyId => {
    setDetailsCompanyId(companyId);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setDetailsCompanyId(null);
  };

  const handleOpenConfirmation = (mode, company) => {
    setConfirmationState({
      open: true,
      mode,
      company
    });
  };

  const handleCloseConfirmation = () => {
    setConfirmationState({
      open: false,
      mode: null,
      company: null
    });
  };

  const handleSubmit = async values => {
    const payload = {
      ...values,
      maxUsers: values.maxUsers === "" ? null : Number(values.maxUsers),
      maxQueues: values.maxQueues === "" ? null : Number(values.maxQueues),
      maxWhatsapps:
        values.maxWhatsapps === "" ? null : Number(values.maxWhatsapps),
      maxSipExtensions:
        values.maxSipExtensions === "" ? null : Number(values.maxSipExtensions),
      monthlyPrice:
        values.monthlyPrice === "" ? null : Number(values.monthlyPrice),
      primaryAdminPassword: values.primaryAdminPassword || undefined,
      trialEndsAt: values.trialEndsAt || null,
      billingStartsAt: values.billingStartsAt || null,
      subscriptionEndsAt: values.subscriptionEndsAt || null,
      lastPaymentAt: values.lastPaymentAt || null,
      sipEnabled: normalizeBoolean(values.sipEnabled),
      sipDomain: values.sipDomain ? String(values.sipDomain).trim().toLowerCase() : null,
      sipTransport: values.sipTransport || "udp",
      sipMediaAddress: values.sipMediaAddress
        ? String(values.sipMediaAddress).trim()
        : null,
      allowContactBlocking: normalizeBoolean(values.allowContactBlocking),
      supportWhatsappNumber: values.supportWhatsappNumber
        ? String(values.supportWhatsappNumber).replace(/\D/g, "")
        : null,
      graceEndsAt: values.graceEndsAt || null,
      scheduledSuspensionAt: values.scheduledSuspensionAt || null,
      suspendedAt: values.suspendedAt || null,
      statusReason: values.statusReason || null,
      internalNotes: values.internalNotes || null
    };

    try {
      if (selectedCompany?.id) {
        const { data } = await api.put(
          `/companies/${selectedCompany.id}`,
          payload
        );
        dispatch({ type: "UPSERT_COMPANY", payload: data });
        toast.success("Empresa atualizada.");
      } else {
        const { data } = await api.post("/companies", payload);
        dispatch({ type: "UPSERT_COMPANY", payload: data.company });
        toast.success("Empresa criada.");
      }

      handleCloseModal();
    } catch (err) {
      toastError(err);
    }
  };

  const handleSavePlan = async values => {
    const payload = {
      name: String(values.planPresetName || "").trim(),
      plan: values.plan,
      maxUsers: values.maxUsers === "" ? null : Number(values.maxUsers),
      maxQueues: values.maxQueues === "" ? null : Number(values.maxQueues),
      maxWhatsapps:
        values.maxWhatsapps === "" ? null : Number(values.maxWhatsapps),
      maxSipExtensions:
        values.maxSipExtensions === "" ? null : Number(values.maxSipExtensions),
      monthlyPrice:
        values.monthlyPrice === "" ? null : Number(values.monthlyPrice),
      currency: values.currency || "BRL",
      supportWhatsappNumber: values.supportWhatsappNumber
        ? String(values.supportWhatsappNumber).replace(/\D/g, "")
        : null,
      connectionPlan: values.connectionPlan || {}
    };

    if (!payload.name) {
      toast.error("Informe um nome para o plano.");
      return;
    }

    try {
      const { data } = await api.post("/companies/plans", payload);
      setCompanyPlans(current => [...current, data].sort((a, b) => a.name.localeCompare(b.name)));
      toast.success("Plano criado.");
    } catch (err) {
      toastError(err);
    }
  };

  const refreshSummary = async () => {
    try {
      const { data } = await api.get("/companies/dashboard/summary");
      setSummary(data);
    } catch (err) {
      toastError(err);
    }
  };

  const handleArchiveCompany = async company => {
    setCompanyActionLoading(true);

    try {
      const { data } = await api.post(`/companies/${company.id}/archive`);
      dispatch({ type: "UPSERT_COMPANY", payload: data });
      await refreshSummary();
      handleCloseDetails();
      toast.success("Empresa arquivada.");
    } catch (err) {
      toastError(err);
    } finally {
      setCompanyActionLoading(false);
      handleCloseConfirmation();
    }
  };

  const handleDeleteCompany = async company => {
    setCompanyActionLoading(true);

    try {
      await api.delete(`/companies/${company.id}`);
      dispatch({ type: "REMOVE_COMPANY", payload: company.id });
      await refreshSummary();
      handleCloseDetails();
      toast.success("Empresa excluida.");
    } catch (err) {
      toastError(err);
    } finally {
      setCompanyActionLoading(false);
      handleCloseConfirmation();
    }
  };

  const refreshMediaStorage = async () => {
    try {
      const { data } = await api.get("/companies/media-storage/summary", {
        params: { thresholdDays: 90 }
      });
      setMediaStorage(normalizeMediaStorage(data));
    } catch (err) {
      toastError(err);
    }
  };

  const handleCleanupMediaStorage = async () => {
    setCompanyActionLoading(true);

    try {
      const { data } = await api.post("/companies/media-storage/cleanup", {
        thresholdDays: 90
      });
      setMediaStorage(normalizeMediaStorage(data));
      toast.success(
        `Limpeza concluida: ${data.deletedFiles || 0} arquivos removidos.`
      );
    } catch (err) {
      toastError(err);
    } finally {
      setCompanyActionLoading(false);
      handleCloseConfirmation();
    }
  };

  const handlePublicMediaStorageChange = event => {
    const { name, value, checked, type } = event.target;
    setPublicMediaStorage(current => ({
      ...current,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSavePublicMediaStorage = async () => {
    setCompanyActionLoading(true);

    try {
      const { data } = await api.put(
        "/companies/media-storage/settings",
        publicMediaStorage
      );
      setPublicMediaStorage(current => ({
        ...current,
        ...data
      }));
      toast.success("Armazenamento de mídias atualizado.");
    } catch (err) {
      toastError(err);
    } finally {
      setCompanyActionLoading(false);
    }
  };

  const handleTestPublicMediaStorage = async () => {
    setCompanyActionLoading(true);

    try {
      const { data } = await api.post(
        "/companies/media-storage/settings/test",
        publicMediaStorage
      );
      toast.success(
        `Conexão validada com sucesso no bucket ${data.bucket || publicMediaStorage.bucket}.`
      );
    } catch (err) {
      toastError(err);
    } finally {
      setCompanyActionLoading(false);
    }
  };

  const handleRecordingStorageChange = event => {
    const { name, value, checked, type } = event.target;
    setRecordingStorage(current => ({
      ...current,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSaveRecordingStorage = async () => {
    setCompanyActionLoading(true);

    try {
      const { data } = await api.put(
        "/companies/call-recording/storage",
        recordingStorage
      );
      setRecordingStorage(current => ({
        ...current,
        ...data
      }));
      toast.success("Armazenamento de gravações atualizado.");
    } catch (err) {
      toastError(err);
    } finally {
      setCompanyActionLoading(false);
    }
  };

  const handleTestRecordingStorage = async () => {
    setCompanyActionLoading(true);

    try {
      const { data } = await api.post(
        "/companies/call-recording/storage/test",
        recordingStorage
      );
      toast.success(
        `Conexão validada com sucesso no bucket ${data.bucket || recordingStorage.bucket}.`
      );
    } catch (err) {
      toastError(err);
    } finally {
      setCompanyActionLoading(false);
    }
  };

  const installationLicenseMessage = installationLicenseStatus?.reason
    ? installationLicenseStatus.reason
    : installationLicenseStatus?.expiresAt
    ? `Validade até ${new Date(installationLicenseStatus.expiresAt).toLocaleString(
        "pt-BR"
      )}`
    : "Sem data de expiração informada.";
  const installationBackendVersion = String(
    installationLicenseStatus?.backendVersion || "desconhecida"
  );

  return (
    <MainContainer scrollY="visible" fullHeight={false}>
      <ConfirmationModal
        title={
          confirmationState.mode === "archive"
            ? "Arquivar empresa"
            : confirmationState.mode === "cleanupMedia"
            ? "Limpar mídia antiga"
            : "Excluir empresa"
        }
        open={confirmationState.open}
        onClose={handleCloseConfirmation}
        onConfirm={() => {
          if (confirmationState.mode === "cleanupMedia") {
            handleCleanupMediaStorage();
            return;
          }

          if (!confirmationState.company) {
            return;
          }

          if (confirmationState.mode === "archive") {
            handleArchiveCompany(confirmationState.company);
            return;
          }

          handleDeleteCompany(confirmationState.company);
        }}
      >
        {confirmationState.mode === "archive"
          ? `A empresa ${confirmationState.company?.name || ""} sera bloqueada para acesso e mantida arquivada por 13 meses. Deseja continuar?`
          : confirmationState.mode === "cleanupMedia"
          ? "Vamos remover da pasta public os arquivos de mídia com mais de 90 dias. Deseja continuar?"
          : `A empresa ${confirmationState.company?.name || ""} sera removida em definitivo com os dados vinculados no banco. Deseja continuar?`}
      </ConfirmationModal>

      <CompanyDetailsDialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        companyId={detailsCompanyId}
        actionLoading={companyActionLoading}
        onArchiveCompany={company => handleOpenConfirmation("archive", company)}
        onDeleteCompany={company => handleOpenConfirmation("delete", company)}
      />

      <CompanyModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        company={selectedCompany}
        companyPlans={companyPlans}
        onSavePlan={handleSavePlan}
      />

      <MainHeader>
        <Title>Empresas</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="contained" color="primary" onClick={handleOpenCreate}>
            Nova empresa
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {installationLicenseStatus ? (
        <Paper
          variant="outlined"
          style={{
            marginBottom: 16,
            padding: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap"
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>
              Licenca da instalação
            </div>
            <div style={{ fontSize: 12, color: "#667085", marginTop: 4 }}>
              {installationLicenseMessage}
            </div>
            <div style={{ fontSize: 12, color: "#667085", marginTop: 2 }}>
              Versão da plataforma: {installationBackendVersion}
            </div>
          </div>
          <Chip
            size="small"
            color={installationLicenseStatus.valid ? "primary" : "secondary"}
            label={
              installationLicenseStatus.valid
                ? installationLicenseStatus.degraded
                  ? "Válida (degradado)"
                  : "Válida"
                : "Inválida"
            }
          />
        </Paper>
      ) : null}

      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={12} sm={6} md={2}>
          <SummaryCard label="Empresas" value={summary.totalCompanies} />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <SummaryCard label="Ativas" value={summary.activeCompanies} />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <SummaryCard label="MRR ativo" value={formatCurrency(summary.activeMrr)} />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <SummaryCard
            label="Em atraso"
            value={`${summary.pastDueCompanies} (${formatCurrency(
              summary.overdueAmount
            )})`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <SummaryCard label="Trials vencendo" value={summary.trialsEndingSoon} />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <SummaryCard
            label="Renovações 7 dias"
            value={summary.renewalsDueSoon}
          />
        </Grid>
      </Grid>

      <Paper
        variant="outlined"
        style={{
          marginBottom: 16,
          padding: 16
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 12
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>
              Armazenamento de mídia (pasta public)
            </div>
            <div style={{ fontSize: 12, color: "#667085", marginTop: 4 }}>
              Total local: {formatBytes(mediaStorage.totalBytes)} em{" "}
              {mediaStorage.totalFiles || 0} arquivos. Antigos ({mediaStorage.thresholdDays || 90} dias):{" "}
              {formatBytes(mediaStorage.oldBytes)} em {mediaStorage.oldFiles || 0} arquivos.
            </div>
            <div style={{ fontSize: 12, color: "#667085", marginTop: 4 }}>
              O S3 vale para novas mídias da pasta public. Arquivos antigos continuam acessíveis no armazenamento local.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              variant="outlined"
              onClick={refreshMediaStorage}
              disabled={companyActionLoading}
            >
              Atualizar
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => handleOpenConfirmation("cleanupMedia", null)}
              disabled={companyActionLoading}
            >
              Limpar +90 dias
            </Button>
          </div>
        </div>

        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  name="enabled"
                  checked={Boolean(publicMediaStorage.enabled)}
                  onChange={handlePublicMediaStorageChange}
                />
              }
              label="Salvar mídias"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              variant="outlined"
              size="small"
              name="provider"
              label="Armazenamento"
              value={publicMediaStorage.provider}
              onChange={handlePublicMediaStorageChange}
            >
              <MenuItem value="local">Local</MenuItem>
              <MenuItem value="s3">S3 compatível</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              name="prefix"
              label="Prefixo/pasta"
              value={publicMediaStorage.prefix}
              onChange={handlePublicMediaStorageChange}
              placeholder="public"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  name="deleteLocalAfterUpload"
                  checked={Boolean(publicMediaStorage.deleteLocalAfterUpload)}
                  onChange={handlePublicMediaStorageChange}
                />
              }
              label="Apagar local após upload"
            />
          </Grid>
          {publicMediaStorage.provider === "s3" ? (
            <>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  name="bucket"
                  label="Bucket"
                  value={publicMediaStorage.bucket}
                  onChange={handlePublicMediaStorageChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  name="region"
                  label="Região"
                  value={publicMediaStorage.region}
                  onChange={handlePublicMediaStorageChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  name="endpoint"
                  label="Endpoint opcional"
                  value={publicMediaStorage.endpoint}
                  onChange={handlePublicMediaStorageChange}
                  placeholder="https://s3.us-east-005.backblazeb2.com"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  name="accessKeyId"
                  label="Access key"
                  value={publicMediaStorage.accessKeyId}
                  onChange={handlePublicMediaStorageChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  type="password"
                  name="secretAccessKey"
                  label="Secret key"
                  value={publicMediaStorage.secretAccessKey}
                  onChange={handlePublicMediaStorageChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  name="publicBaseUrl"
                  label="URL pública/base CDN"
                  value={publicMediaStorage.publicBaseUrl}
                  onChange={handlePublicMediaStorageChange}
                  placeholder="https://cdn.seudominio.com/public"
                  helperText="O navegador usará essa URL para abrir anexos e imagens."
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      name="forcePathStyle"
                      checked={Boolean(publicMediaStorage.forcePathStyle)}
                      onChange={handlePublicMediaStorageChange}
                    />
                  }
                  label="Path style"
                />
              </Grid>
            </>
          ) : null}
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSavePublicMediaStorage}
              disabled={companyActionLoading}
            >
              Salvar mídias
            </Button>
            {publicMediaStorage.provider === "s3" ? (
              <Button
                variant="outlined"
                color="primary"
                onClick={handleTestPublicMediaStorage}
                disabled={companyActionLoading}
                style={{ marginLeft: 8 }}
              >
                Testar conexão
              </Button>
            ) : null}
          </Grid>
        </Grid>
      </Paper>

      <Paper
        variant="outlined"
        style={{
          marginBottom: 16,
          padding: 16
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 700 }}>
          Gravações de chamadas
        </div>
        <div style={{ fontSize: 12, color: "#667085", marginTop: 4, marginBottom: 12 }}>
          Configure onde as gravações serão guardadas. Para Backblaze B2, use região como us-east-005 e endpoint com https://.
        </div>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  name="enabled"
                  checked={Boolean(recordingStorage.enabled)}
                  onChange={handleRecordingStorageChange}
                />
              }
              label="Gravar chamadas"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              variant="outlined"
              size="small"
              name="provider"
              label="Armazenamento"
              value={recordingStorage.provider}
              onChange={handleRecordingStorageChange}
            >
              <MenuItem value="local">Local</MenuItem>
              <MenuItem value="s3">S3 compatível</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              name="prefix"
              label="Prefixo/pasta"
              value={recordingStorage.prefix}
              onChange={handleRecordingStorageChange}
              placeholder="companies"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              type="number"
              name="signedUrlExpiresSeconds"
              label="Expiração do link (s)"
              value={recordingStorage.signedUrlExpiresSeconds}
              onChange={handleRecordingStorageChange}
            />
          </Grid>
          {recordingStorage.provider === "s3" ? (
            <>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  name="bucket"
                  label="Bucket"
                  value={recordingStorage.bucket}
                  onChange={handleRecordingStorageChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  name="region"
                  label="Região"
                  value={recordingStorage.region}
                  onChange={handleRecordingStorageChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  name="endpoint"
                  label="Endpoint opcional"
                  value={recordingStorage.endpoint}
                  onChange={handleRecordingStorageChange}
                  placeholder="https://s3.us-east-005.backblazeb2.com"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  name="accessKeyId"
                  label="Access key"
                  value={recordingStorage.accessKeyId}
                  onChange={handleRecordingStorageChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  type="password"
                  name="secretAccessKey"
                  label="Secret key"
                  value={recordingStorage.secretAccessKey}
                  onChange={handleRecordingStorageChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      name="forcePathStyle"
                      checked={Boolean(recordingStorage.forcePathStyle)}
                      onChange={handleRecordingStorageChange}
                    />
                  }
                  label="Path style"
                />
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      name="deleteLocalAfterUpload"
                      checked={Boolean(recordingStorage.deleteLocalAfterUpload)}
                      onChange={handleRecordingStorageChange}
                    />
                  }
                  label="Apagar local após upload"
                />
              </Grid>
            </>
          ) : null}
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveRecordingStorage}
              disabled={companyActionLoading}
            >
              Salvar gravações
            </Button>
            {recordingStorage.provider === "s3" ? (
              <Button
                variant="outlined"
                color="primary"
                onClick={handleTestRecordingStorage}
                disabled={companyActionLoading}
                style={{ marginLeft: 8 }}
              >
                Testar conexão
              </Button>
            ) : null}
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined">
        <TableContainer style={{ overflowX: "auto", overflowY: "hidden" }}>
          <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Empresa</TableCell>
              <TableCell>Plano</TableCell>
              <TableCell>Status comercial</TableCell>
              <TableCell>Mensalidade</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Ultimo acesso</TableCell>
              <TableCell>Admin principal</TableCell>
              <TableCell>Dominio SIP</TableCell>
              <TableCell>Limites</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {companies.map(company => (
              <TableRow key={company.id}>
                <TableCell>{company.id}</TableCell>
                <TableCell>{company.name}</TableCell>
                <TableCell>{company.plan || "custom"}</TableCell>
                <TableCell>{company.subscriptionStatus || "active"}</TableCell>
                <TableCell>
                  {company.monthlyPrice !== null && company.monthlyPrice !== undefined
                    ? formatCurrency(company.monthlyPrice)
                    : "-"}
                </TableCell>
                <TableCell>{company.slug}</TableCell>
                <TableCell>
                  {company.archivedAt
                    ? "Arquivada"
                    : company.status === "active"
                    ? "Ativa"
                    : "Inativa"}
                  {company.archiveExpiresAt ? (
                    <div style={{ fontSize: 12, color: "#667085" }}>
                      até {new Date(company.archiveExpiresAt).toLocaleDateString("pt-BR")}
                    </div>
                  ) : null}
                </TableCell>
                <TableCell>{formatDateTime(company.lastAccessAt)}</TableCell>
                <TableCell>{company.primaryAdmin?.email || "Sem admin"}</TableCell>
                <TableCell>
                  {company.sipEnabled ? company.sipDomain || "-" : "Desabilitado"}
                </TableCell>
                <TableCell>
                  U: {company.metrics?.usersCount || 0}/{company.maxUsers || "-"} | F:{" "}
                  {company.metrics?.queuesCount || 0}/{company.maxQueues || "-"} | C:{" "}
                  {company.metrics?.whatsappsCount || 0}/{company.maxWhatsapps || "-"} | R:{" "}
                  {company.metrics?.sipExtensionsCount || 0}/{company.maxSipExtensions || "-"}
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDetails(company.id)}
                  >
                    <VisibilityIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleEdit(company)}>
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={12} />}
          </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </MainContainer>
  );
};

export default Companies;
